import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Ruler } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "./constants";
import { deriveGarmentSku, getAppliedGarments } from "./printSizing";

/**
 * Completion-stage sizing check. Shown when a job moves to the "Completed" kanban stage.
 * - Confirmed print groups get a quality prompt (Yes / too big / too small + optional corrected size).
 * - Print groups that reached Completed WITHOUT ever being confirmed are flagged as a process gap.
 */
export function CompletionSizingCheckModal({ open, onClose, job }) {
  const jobData = job?.job_data || {};
  const groups = (jobData.artwork_groups || []).filter((g) => g.placement_name);
  const confirmed = groups.filter((g) => g.sizing_confirmed && g.print_width_mm && g.print_height_mm);
  const neverConfirmed = groups.filter((g) => !g.sizing_confirmed);

  // Per-group answer state: { [groupId]: { outcome, note, cw, ch } }
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const setAnswer = (id, patch) =>
    setAnswers((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSubmit = async () => {
    setSaving(true);
    const results = [];
    for (const g of confirmed) {
      const a = answers[g.id];
      if (!a || !a.outcome) continue; // unanswered = skip
      const applied = getAppliedGarments(g, jobData.garments || []);
      const targets = applied.length > 0 ? applied : [null];
      for (const garment of targets) {
        results.push({
          garment_sku: garment ? deriveGarmentSku(garment) : g.garment_sku || "",
          size_range: g.size_range || "",
          placement: g.placement_name,
          outcome: a.outcome,
          corrected_width_mm: a.cw ? parseFloat(a.cw) : null,
          corrected_height_mm: a.ch ? parseFloat(a.ch) : null,
          note: a.note || null,
        });
      }
    }
    try {
      if (results.length > 0) {
        await axios.post(`${API}/sizing/completion-check`, results);
      }
      toast.success("Sizing completion check saved.");
      onClose(true);
    } catch (e) {
      toast.error("Could not save the sizing check.");
    } finally {
      setSaving(false);
    }
  };

  const OUTCOMES = [
    { value: "ok", label: "Yes, as expected", cls: "data-[on=true]:bg-emerald-500 data-[on=true]:text-white data-[on=true]:border-emerald-500" },
    { value: "too_big", label: "Came out too big", cls: "data-[on=true]:bg-amber-500 data-[on=true]:text-white data-[on=true]:border-amber-500" },
    { value: "too_small", label: "Came out too small", cls: "data-[on=true]:bg-amber-500 data-[on=true]:text-white data-[on=true]:border-amber-500" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="completion-sizing-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-blue-500" />
            Sizing check — how did it print?
          </DialogTitle>
          <DialogDescription>
            This job just moved to Completed. Confirm how the finished order came out so your sizing library stays accurate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {confirmed.length === 0 && neverConfirmed.length === 0 && (
            <p className="text-sm text-muted-foreground">No print groups with sizing on this job.</p>
          )}

          {confirmed.map((g) => {
            const a = answers[g.id] || {};
            return (
              <div key={g.id} className="border rounded-lg p-3 space-y-2" data-testid={`completion-group-${g.id}`}>
                <p className="text-sm">
                  You confirmed <strong>{g.placement_name}</strong>
                  {g.size_range ? ` (${g.size_range})` : ""} at{" "}
                  <strong>{g.print_width_mm}mm × {g.print_height_mm}mm</strong>. Did the finished order come out as expected?
                </p>
                <div className="flex flex-wrap gap-2">
                  {OUTCOMES.map((o) => (
                    <button
                      key={o.value}
                      data-on={a.outcome === o.value}
                      onClick={() => setAnswer(g.id, { outcome: o.value })}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${o.cls} border-gray-200`}
                      data-testid={`completion-${g.id}-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {(a.outcome === "too_big" || a.outcome === "too_small") && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Corrected width (mm) — optional</Label>
                      <Input
                        type="number" min="0" placeholder="e.g. 110"
                        value={a.cw ?? ""}
                        onChange={(e) => setAnswer(g.id, { cw: e.target.value })}
                        className="h-8 font-mono text-sm"
                        data-testid={`completion-${g.id}-cw`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Corrected height (mm) — optional</Label>
                      <Input
                        type="number" min="0" placeholder="e.g. 80"
                        value={a.ch ?? ""}
                        onChange={(e) => setAnswer(g.id, { ch: e.target.value })}
                        className="h-8 font-mono text-sm"
                        data-testid={`completion-${g.id}-ch`}
                      />
                    </div>
                  </div>
                )}
                <Input
                  placeholder="Optional note…"
                  value={a.note ?? ""}
                  onChange={(e) => setAnswer(g.id, { note: e.target.value })}
                  className="h-8 text-sm"
                  data-testid={`completion-${g.id}-note`}
                />
              </div>
            );
          })}

          {neverConfirmed.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="completion-never-confirmed">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Sizing was never confirmed for:</p>
                  <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                    {neverConfirmed.map((g) => (
                      <li key={g.id}>{g.placement_name}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-600 mt-1">Process gap — no size recorded for future reference.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onClose(false)} data-testid="completion-skip-btn">
            Skip
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="completion-submit-btn">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : "Save check"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompletionSizingCheckModal;
